import { Component } from '@theme/component';

/** Pixels of slack when detecting the scroll ends (sub-pixel scroll positions). */
const SCROLL_TOLERANCE = 1;

/**
 * @typedef {Object} BundleBuilderCartStartersScrollerRefs
 * @property {HTMLElement} list
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} progressThumb
 */

/**
 * Horizontal scroll strip for the cart starter pills. Shows previous/next arrows and a
 * scroll-position bar only while the pills overflow (mobile/tablet), via the `overflowing` attribute.
 *
 * @extends {Component<BundleBuilderCartStartersScrollerRefs>}
 */
export class BundleBuilderCartStartersScroller extends Component {
  requiredRefs = ['list', 'previous', 'next', 'progressThumb'];

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    this.refs.list.addEventListener('scroll', this.#update, { passive: true });
    this.#resizeObserver = new ResizeObserver(this.#update);
    this.#resizeObserver.observe(this.refs.list);
    this.#update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.list?.removeEventListener('scroll', this.#update);
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
  }

  previous() {
    this.#scroll(-1);
  }

  next() {
    this.#scroll(1);
  }

  get #isRtl() {
    return getComputedStyle(this.refs.list).direction === 'rtl';
  }

  get #maxScroll() {
    const { list } = this.refs;
    return list.scrollWidth - list.clientWidth;
  }

  /** Scroll distance from the inline start edge, normalized for RTL (where scrollLeft is ≤ 0). */
  get #scrollPosition() {
    return Math.abs(this.refs.list.scrollLeft);
  }

  /**
   * Scrolls by most of a viewport-width of pills. No-op at the matching end, where that
   * arrow is marked aria-disabled.
   * @param {1 | -1} direction - 1 toward the inline end, -1 toward the inline start
   */
  #scroll(direction) {
    if ((direction === -1 && this.#atStart) || (direction === 1 && this.#atEnd)) return;

    const { list } = this.refs;
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
    const inlineSign = this.#isRtl ? -1 : 1;

    list.scrollBy({ left: list.clientWidth * 0.8 * direction * inlineSign, behavior });
  }

  get #atStart() {
    return this.#scrollPosition <= SCROLL_TOLERANCE;
  }

  get #atEnd() {
    return this.#scrollPosition >= this.#maxScroll - SCROLL_TOLERANCE;
  }

  #update = () => {
    const { list, previous, next, progressThumb } = this.refs;
    if (!list || !previous || !next || !progressThumb) return;

    // CSS keys off this attribute to show (display) or remove (display: none) the arrows and bar.
    const overflowing = this.#maxScroll > 1;
    this.toggleAttribute('overflowing', overflowing);
    if (!overflowing) return;

    // aria-disabled (not disabled) keeps a focused arrow focusable when it reaches the end.
    previous.setAttribute('aria-disabled', String(this.#atStart));
    next.setAttribute('aria-disabled', String(this.#atEnd));

    const visibleRatio = list.clientWidth / list.scrollWidth;
    const scrolledRatio = this.#scrollPosition / this.#maxScroll;

    progressThumb.style.setProperty('--thumb-size', `${visibleRatio * 100}%`);
    progressThumb.style.setProperty('--thumb-offset', `${scrolledRatio * (1 - visibleRatio) * 100}%`);
  };
}

if (!customElements.get('bundle-builder-cart-starters-scroller')) {
  customElements.define('bundle-builder-cart-starters-scroller', BundleBuilderCartStartersScroller);
}
